import { z } from 'zod';
import { CAMPAIGN_EMAIL_AUDIENCES, CAMPAIGN_KINDS, CAMPAIGN_SMS_AUDIENCES, type CampaignKind, type CampaignSmsAudience } from '@bulk/types';
import { dateTimeSchema, idSchema, patchOf, publicAssetSchema } from './common';

const localizedText = z.record(z.string(), z.string());
const percentage = z.number().int().min(0).max(100);

export const campaignKindSchema = z.enum(CAMPAIGN_KINDS);

const bonusDays = z.number().int().min(0).max(365);

export const campaignPlanPctSchema = z.object({
  planId: idSchema,
  pct: percentage,
  bonusDays: bonusDays.default(0).describe('BONUS_DAYS: extra days on this plan, instead of the campaign default'),
});

export const PROMO_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,31}$/;
export const promoCodeSchema = z.string().trim().regex(PROMO_CODE_PATTERN, 'A code is 3 to 32 letters, digits, - or _');
export type CampaignPlanPct = z.infer<typeof campaignPlanPctSchema>;

export const campaignCtaSchema = z.object({
  label: localizedText,
  href: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .regex(/^(https?:\/\/|\/(?!\/)|#|mailto:|tel:)/i, 'Use a web address (https://…), a page of the site (/…), a section (#…), mailto: or tel:'),
});
export type CampaignCta = z.infer<typeof campaignCtaSchema>;

const campaignFieldsSchema = z.object({
  title: localizedText,
  description: localizedText.default({}),
  startsAt: dateTimeSchema,
  endsAt: dateTimeSchema,
  defaultPct: percentage.default(0),
  newMemberPct: percentage.default(0),
  announceDays: z.number().int().min(0).max(120).default(14),
  active: z.boolean().default(true),
  plans: z.array(campaignPlanPctSchema).max(200).default([]),
  imageAssetId: idSchema.nullable().default(null),
  cta: campaignCtaSchema.nullable().default(null),
  code: promoCodeSchema.nullable().default(null).describe('PROMO_CODE: what buyers type, matched without regard to case; unique among the gym\'s codes running at the same time'),
  usageLimit: z.number().int().min(1).max(1_000_000).nullable().default(null).describe('PROMO_CODE: purchases in all; null for no limit'),
  perMemberLimit: z.number().int().min(1).max(100).default(1).describe('PROMO_CODE: purchases per member'),
  defaultBonusDays: bonusDays.default(0).describe('BONUS_DAYS: extra days added to a membership bought while the campaign runs'),
});

const hasText = (text: Record<string, string>) => Object.values(text).some((value) => value.trim().length > 0);

export function discountsSomething(c: { kind: CampaignKind; defaultPct: number; newMemberPct: number; plans: CampaignPlanPct[] }): boolean {
  if (c.defaultPct > 0 || c.plans.some((p) => p.pct > 0)) return true;
  return c.kind === 'SALE' && c.newMemberPct > 0;
}

export function addsDays(c: { defaultBonusDays: number; plans: CampaignPlanPct[] }): boolean {
  return c.defaultBonusDays > 0 || c.plans.some((p) => p.bonusDays > 0);
}

/**
 * The rules for a whole campaign. They run on create and again on the row a patch produces, so a
 * patch cannot leave a campaign that could not have been created. Only a campaign that is switched
 * on has to discount something: switching one off is always possible, and a disabled copy is a draft.
 */
function checkCampaign(c: z.output<typeof campaignFieldsSchema> & { kind: CampaignKind }, ctx: z.RefinementCtx): void {
  if (new Date(c.endsAt) <= new Date(c.startsAt)) ctx.addIssue({ code: 'custom', message: 'The campaign must end after it starts', path: ['endsAt'] });
  if (new Set(c.plans.map((p) => p.planId)).size !== c.plans.length) ctx.addIssue({ code: 'custom', message: 'A plan can carry only one percentage in a campaign', path: ['plans'] });
  if (c.kind === 'PROMO_CODE' && !c.code) ctx.addIssue({ code: 'custom', message: 'A promo code campaign needs its code', path: ['code'] });
  if (c.kind === 'ANNOUNCEMENT') {
    if (!hasText(c.title)) ctx.addIssue({ code: 'custom', message: 'An announcement needs a title', path: ['title'] });
    if (c.cta && !hasText(c.cta.label)) ctx.addIssue({ code: 'custom', message: 'A button needs a label', path: ['cta', 'label'] });
  } else if (c.kind === 'BONUS_DAYS') {
    if (c.active && !addsDays(c)) ctx.addIssue({ code: 'custom', message: 'Give the campaign some bonus days', path: ['defaultBonusDays'] });
  } else if (c.active && !discountsSomething(c)) {
    ctx.addIssue({ code: 'custom', message: 'Give the campaign a discount above 0%', path: ['defaultPct'] });
  }
}

export const createCampaignSchema = campaignFieldsSchema.extend({ kind: campaignKindSchema }).superRefine(checkCampaign);
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

export const updateCampaignSchema = patchOf(campaignFieldsSchema).extend({
  kind: z.never({ error: 'The kind of a campaign is chosen when it is created and cannot change' }).optional(),
});
export type UpdateCampaignInput = z.infer<typeof updateCampaignSchema>;

export const campaignSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  kind: campaignKindSchema,
  title: localizedText,
  description: localizedText,
  startsAt: z.string(),
  endsAt: z.string(),
  defaultPct: z.number().int(),
  newMemberPct: z.number().int(),
  announceDays: z.number().int(),
  active: z.boolean(),
  plans: z.array(campaignPlanPctSchema),
  imageAssetId: idSchema.nullable(),
  cta: campaignCtaSchema.nullable(),
  code: z.string().nullable(),
  usageLimit: z.number().int().nullable(),
  perMemberLimit: z.number().int(),
  defaultBonusDays: z.number().int(),
  redemptions: z.number().int().describe('PROMO_CODE: confirmed purchases that used the code'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CampaignDto = z.infer<typeof campaignSchema>;

export const campaignStatusSchema = z.enum(['active', 'upcoming', 'none']);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

export const publicCampaignSchema = z.object({
  id: idSchema,
  title: localizedText,
  description: localizedText,
  startsAt: z.string(),
  endsAt: z.string(),
  defaultPct: z.number().int(),
  newMemberPct: z.number().int(),
});

export const campaignStateSchema = z.object({
  status: campaignStatusSchema,
  campaign: publicCampaignSchema.nullable(),
});
export type CampaignState = z.infer<typeof campaignStateSchema>;

export const publicCampaignEntrySchema = publicCampaignSchema.extend({
  kind: campaignKindSchema,
  status: z.enum(['active', 'upcoming']),
  maxPct: z.number().int().describe('The largest percentage any plan gets, for "up to" wording'),
  image: publicAssetSchema.nullable(),
  cta: campaignCtaSchema.nullable(),
});
export type PublicCampaignEntry = z.infer<typeof publicCampaignEntrySchema>;

export const publicBonusSchema = publicCampaignSchema.extend({
  status: z.enum(['active']),
  defaultBonusDays: z.number().int(),
  maxBonusDays: z.number().int().describe('The most days any plan gets, for "up to" wording'),
  plans: z.array(z.object({ planId: idSchema, bonusDays: z.number().int() })),
});
export type PublicBonus = z.infer<typeof publicBonusSchema>;

export const publicCampaignsSchema = z.object({
  bonus: publicBonusSchema.nullable().default(null).describe('The live bonus-days campaign: memberships bought now run longer'),
  sale: publicCampaignEntrySchema.nullable().describe('The live sale, or the next one once its announce window has opened'),
  newMember: publicCampaignEntrySchema.nullable().describe('The live offer for people who have never held a membership here'),
  announcements: z.array(publicCampaignEntrySchema).describe('Live or announced announcements, latest start first, at most three'),
});
export type PublicCampaigns = z.infer<typeof publicCampaignsSchema>;

export const campaignListQuerySchema = z.object({
  includePast: z.stringbool().default(false),
});

export const planPriceSchema = z.object({
  planId: idSchema,
  listPriceMinor: z.number().int(),
  priceMinor: z.number().int(),
  discountPct: z.number().int(),
  campaignId: idSchema.nullable(),
  campaignKind: campaignKindSchema.nullable(),
  bonusDays: z.number().int().describe('Extra days a membership bought now gets from a bonus-days campaign; 0 for none'),
  bonusCampaignId: idSchema.nullable(),
});
export type PlanPriceDto = z.infer<typeof planPriceSchema>;

export const planPricesSchema = z.object({
  newMember: z.boolean().describe('The buyer has never held a membership at this gym, so a new-member offer applies to them'),
  prices: z.array(planPriceSchema),
});
export type PlanPricesDto = z.infer<typeof planPricesSchema>;

export const memberOffersSchema = planPricesSchema.extend({
  campaigns: publicCampaignsSchema.describe('What the member is offered: the new-member offer only when it applies to them'),
});
export type MemberOffersDto = z.infer<typeof memberOffersSchema>;

export const deskPricesQuerySchema = z.object({
  memberId: idSchema.optional().describe('Omit for someone who is not a member yet'),
  promoCode: z.string().trim().min(1).max(32).optional().describe('Price with this code too; 422 PROMO_CODE_INVALID with a reason when it gives nothing'),
});

export const memberOffersQuerySchema = z.object({
  promoCode: z.string().trim().min(1).max(32).optional().describe('Price with this code too; 422 PROMO_CODE_INVALID with a reason when it gives nothing'),
});

export const CAMPAIGN_SMS_TEXT_MAX = 480;

const smsAudienceFields = {
  audience: z.enum(CAMPAIGN_SMS_AUDIENCES),
  segmentId: idSchema.optional().describe('The saved segment to text, with audience SEGMENT; only its members who agreed to offers are texted'),
};

const segmentNamedWithItsAudience = (v: { audience: CampaignSmsAudience; segmentId?: string | undefined }) => (v.audience === 'SEGMENT') === (v.segmentId !== undefined);
const segmentMessage = { path: ['segmentId'], message: 'Choose a segment for the audience SEGMENT, and only for it' };

export const campaignSmsInputSchema = z
  .object({
    texts: z.object({
      en: z.string().trim().max(CAMPAIGN_SMS_TEXT_MAX).default(''),
      ka: z.string().trim().max(CAMPAIGN_SMS_TEXT_MAX).default(''),
    }),
    ...smsAudienceFields,
  })
  .refine((v) => v.texts.en !== '' || v.texts.ka !== '', { path: ['texts'], message: 'Write the text in at least one language' })
  .refine(segmentNamedWithItsAudience, segmentMessage);
export type CampaignSmsInput = z.infer<typeof campaignSmsInputSchema>;

export const campaignSmsPreviewQuerySchema = z.object(smsAudienceFields).refine(segmentNamedWithItsAudience, segmentMessage);
export type CampaignSmsAudienceInput = z.infer<typeof campaignSmsPreviewQuerySchema>;

export const CAMPAIGN_SMS_BLOCKERS = ['NO_SMS_ACCOUNT', 'NO_OPT_OUT_LINE'] as const;

export const campaignSmsPreviewSchema = z.object({
  recipients: z.number().int(),
  byLocale: z.object({ en: z.number().int(), ka: z.number().int() }),
  alreadyTexted: z.number().int().describe('Members in the audience who already received this campaign by SMS; they are skipped'),
  prefix: z.string().describe('What goes before the text: the gym name and a colon'),
  optOutLine: z.string().describe('What goes after the text; empty while the gym has not set it'),
  blocker: z.enum(CAMPAIGN_SMS_BLOCKERS).nullable(),
});
export type CampaignSmsPreviewDto = z.infer<typeof campaignSmsPreviewSchema>;

export const campaignSmsSendSchema = z.object({
  id: idSchema,
  campaignId: idSchema,
  texts: z.object({ en: z.string(), ka: z.string() }),
  audience: z.enum(CAMPAIGN_SMS_AUDIENCES),
  segmentId: idSchema.nullable(),
  segmentName: z.string().nullable().describe('The segment texted, as it is named now; null for another audience or once the segment is deleted'),
  recipients: z.number().int(),
  queued: z.number().int(),
  sent: z.number().int(),
  failed: z.number().int(),
  sentByName: z.string().nullable(),
  createdAt: z.string(),
});
export type CampaignSmsSendDto = z.infer<typeof campaignSmsSendSchema>;

export const CAMPAIGN_EMAIL_SUBJECT_MAX = 150;
export const CAMPAIGN_EMAIL_TEXT_MAX = 5000;

const emailAudienceFields = {
  audience: z.enum(CAMPAIGN_EMAIL_AUDIENCES),
  segmentId: idSchema.optional().describe('The saved segment to email, with audience SEGMENT; only its members who agreed to offers by email get it'),
};

const bothLanguages = (max: number) =>
  z.object({
    en: z.string().trim().max(max).default(''),
    ka: z.string().trim().max(max).default(''),
  });

export const campaignEmailInputSchema = z
  .object({
    subjects: bothLanguages(CAMPAIGN_EMAIL_SUBJECT_MAX),
    texts: bothLanguages(CAMPAIGN_EMAIL_TEXT_MAX),
    ...emailAudienceFields,
  })
  .refine((v) => v.texts.en !== '' || v.texts.ka !== '', { path: ['texts'], message: 'Write the email in at least one language' })
  .refine((v) => (v.texts.en === '' || v.subjects.en !== '') && (v.texts.ka === '' || v.subjects.ka !== ''), { path: ['subjects'], message: 'Give every language you wrote the email in a subject' })
  .refine(segmentNamedWithItsAudience, segmentMessage);
export type CampaignEmailInput = z.infer<typeof campaignEmailInputSchema>;

export const campaignEmailPreviewQuerySchema = z.object(emailAudienceFields).refine(segmentNamedWithItsAudience, segmentMessage);
export type CampaignEmailAudienceInput = z.infer<typeof campaignEmailPreviewQuerySchema>;

export const CAMPAIGN_EMAIL_BLOCKERS = ['NO_EMAIL_ACCOUNT', 'PRACTICE_GYM'] as const;

export const campaignEmailPreviewSchema = z.object({
  recipients: z.number().int(),
  byLocale: z.object({ en: z.number().int(), ka: z.number().int() }),
  alreadyEmailed: z.number().int().describe('Members in the audience who already received this campaign by email; they are skipped'),
  blocker: z.enum(CAMPAIGN_EMAIL_BLOCKERS).nullable().describe("NO_EMAIL_ACCOUNT while the gym's own email account is not connected and tested; PRACTICE_GYM in a practice gym"),
});
export type CampaignEmailPreviewDto = z.infer<typeof campaignEmailPreviewSchema>;

export const campaignEmailSendSchema = z.object({
  id: idSchema,
  campaignId: idSchema,
  subjects: z.object({ en: z.string(), ka: z.string() }),
  texts: z.object({ en: z.string(), ka: z.string() }),
  audience: z.enum(CAMPAIGN_EMAIL_AUDIENCES),
  segmentId: idSchema.nullable(),
  segmentName: z.string().nullable().describe('The segment emailed, as it is named now; null for another audience or once the segment is deleted'),
  recipients: z.number().int(),
  queued: z.number().int(),
  sent: z.number().int(),
  failed: z.number().int(),
  sentByName: z.string().nullable(),
  createdAt: z.string(),
});
export type CampaignEmailSendDto = z.infer<typeof campaignEmailSendSchema>;

export const stopEmailOffersSchema = z.object({
  gymName: z.string().describe('The gym the member will get no more offers by email from'),
});
export type StopEmailOffersDto = z.infer<typeof stopEmailOffersSchema>;
